import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

const COLUMNS_PER_ROW = 3;
const GRID_GAP = 16;
const CARD_ASPECT_RATIO = 200 / 133;

export function useImagesGridVirtualizer({
  itemCount,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  itemCount: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      throw new Error(
        "scrollContainerRef.current is not defined, did you add an early return to this component?",
      );
    }
    const observer = new ResizeObserver((entries) => {
      const size = entries[0]?.contentBoxSize?.[0];
      if (size) setContainerWidth(size.inlineSize);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const totalRows = Math.ceil(itemCount / COLUMNS_PER_ROW);

  const estimatedRowHeight = useMemo(() => {
    if (containerWidth === 0) return 0;
    const itemWidth =
      (containerWidth - (COLUMNS_PER_ROW - 1) * GRID_GAP) / COLUMNS_PER_ROW;
    return itemWidth / CARD_ASPECT_RATIO + GRID_GAP;
  }, [containerWidth]);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? totalRows + 1 : totalRows,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 5,
    enabled: estimatedRowHeight > 0,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [rowVirtualizer, estimatedRowHeight]);

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (estimatedRowHeight === 0) return;

    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;

    if (lastItem.index >= totalRows - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    totalRows,
    estimatedRowHeight,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  return {
    scrollContainerRef,
    columnsPerRow: COLUMNS_PER_ROW,
    totalRows,
    rowVirtualizer,
    virtualItems,
  };
}
